import type { ApiResponse } from "../../../shared/api";
import type { ProcessedTshirtImage } from "../../../shared/image-processing";
import type { TshirtMetadata } from "../../../shared/tshirts";
import { createClientId } from "../identity/create-id";
import type { CollectionImage, CollectionItem } from "./state";

type UploadResponse = {
  readonly tshirtId: string;
  readonly objectKey: string;
  readonly uploadUrl: string;
  readonly expiresAt: string;
  readonly headers: Readonly<Record<string, string>>;
};

type SaveImageProgress = (message: string) => void;

type TshirtListResponse = {
  readonly tshirts: readonly TshirtMetadata[];
};

export type AuthSession = {
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly name: string;
    readonly image?: string | null;
  };
};

type SignInResponse = {
  readonly url?: string;
};

const guestStorageKey = "tshirt-memory-guest-id";

export class AuthRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthRequiredError";
  }
}

const readGuestId = (): string => {
  const existingGuestId = window.localStorage.getItem(guestStorageKey);
  const guestId = existingGuestId ?? createClientId();

  existingGuestId
    ? null
    : window.localStorage.setItem(guestStorageKey, guestId);

  return guestId;
};

const isLocalDevelopmentHost = (): boolean =>
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname === "::1" ||
  window.location.hostname.endsWith(".local");

export const isDevGuestSaveEnabled = (): boolean =>
  import.meta.env.DEV &&
  import.meta.env.VITE_ALLOW_DEV_GUEST_USER === "true" &&
  isLocalDevelopmentHost();

const createJsonHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  ...(isDevGuestSaveEnabled()
    ? {
        "X-Tshirt-Memory-Guest-Id": readGuestId(),
      }
    : {}),
});

const readPublicBaseUrl = (): string =>
  import.meta.env.VITE_R2_PUBLIC_BASE_URL ?? "";

const joinUrlPath = (baseUrl: string, objectKey: string): string =>
  `${baseUrl.replace(/\/$/, "")}/${objectKey
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;

const createLocalImageUrl = (objectKey: string): string =>
  `/api/uploads/local?objectKey=${encodeURIComponent(objectKey)}`;

const createImageUrl = (objectKey: string): string =>
  isLocalDevelopmentHost()
    ? createLocalImageUrl(objectKey)
    : readPublicBaseUrl()
    ? joinUrlPath(readPublicBaseUrl(), objectKey)
    : "";

const createCollectionImage = (
  image: TshirtMetadata["images"][number],
): CollectionImage => ({
  side: image.side,
  imageUrl: createImageUrl(image.objectKey),
});

const createCollectionItem = (tshirt: TshirtMetadata): CollectionItem => ({
  id: tshirt.id,
  name: tshirt.name,
  images: tshirt.images.reduce<
    Partial<Record<CollectionImage["side"], CollectionImage>>
  >(
    (images, image) => ({
      ...images,
      [image.side]: createCollectionImage(image),
    }),
    {},
  ),
  createdAt: tshirt.createdAt,
});

const readApiResponse = async <TData>(response: Response): Promise<TData> => {
  const body = (await response.json()) as ApiResponse<TData>;

  return response.ok && body.ok
    ? body.data
    : response.status === 401
      ? Promise.reject(
          new AuthRequiredError(
            body.ok ? "Authentication is required" : body.error.message,
          ),
        )
    : Promise.reject(
        new Error(body.ok ? "API request failed" : body.error.message),
      );
};

const isSessionBody = (body: unknown): body is AuthSession =>
  body !== null &&
  typeof body === "object" &&
  "user" in body &&
  isSessionUser(body.user);

const isSessionUser = (user: unknown): user is AuthSession["user"] =>
  user !== null &&
  typeof user === "object" &&
  "id" in user &&
  "email" in user &&
  "name" in user &&
  typeof user.id === "string" &&
  typeof user.email === "string" &&
  typeof user.name === "string";

export const fetchCurrentAuthSession = async (): Promise<AuthSession | null> =>
  fetch("/api/auth/get-session", {
    method: "GET",
    credentials: "include",
  }).then(async (response) =>
    response.ok
      ? ((await response.json().catch(() => null)) as unknown)
      : null,
  ).then((body) => (isSessionBody(body) ? body : null));

export const startGoogleSignIn = async (): Promise<void> =>
  fetch("/api/auth/sign-in/social", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      provider: "google",
      callbackURL: window.location.href,
    }),
  })
    .then(async (response) =>
      response.ok
        ? ((await response.json().catch(() => ({}))) as SignInResponse)
        : Promise.reject(new Error("Googleログインを開始できませんでした")),
    )
    .then((body) =>
      body.url
        ? window.location.assign(body.url)
        : Promise.reject(new Error("GoogleログインURLを取得できませんでした")),
    );

export const signOut = async (): Promise<void> =>
  fetch("/api/auth/sign-out", {
    method: "POST",
    credentials: "include",
    headers: createJsonHeaders(),
    body: JSON.stringify({}),
  }).then((response) =>
    response.ok
      ? undefined
      : Promise.reject(new Error("ログアウトに失敗しました")),
  );

const requestUpload = async (
  image: ProcessedTshirtImage,
): Promise<UploadResponse> =>
  fetch("/api/uploads", {
    method: "POST",
    credentials: "include",
    headers: createJsonHeaders(),
    body: JSON.stringify({
      side: image.side,
      contentType: image.blob.type,
      width: image.width,
      height: image.height,
    }),
  }).then((response) => readApiResponse<UploadResponse>(response));

const putImageToR2 = async (params: {
  readonly image: ProcessedTshirtImage;
  readonly upload: UploadResponse;
}): Promise<void> =>
  fetch(params.upload.uploadUrl, {
    method: "PUT",
    headers: params.upload.headers,
    body: params.image.blob,
  }).then((response) =>
    response.ok
      ? undefined
      : Promise.reject(new Error("R2への画像アップロードに失敗しました")),
  );

const createTshirtMetadata = async (params: {
  readonly image: ProcessedTshirtImage;
  readonly upload: UploadResponse;
}): Promise<TshirtMetadata> =>
  fetch("/api/tshirts", {
    method: "POST",
    credentials: "include",
    headers: createJsonHeaders(),
    body: JSON.stringify({
      id: params.upload.tshirtId,
      name: "名称未設定",
      images: [
        {
          side: params.image.side,
          objectKey: params.upload.objectKey,
          width: params.image.width,
          height: params.image.height,
        },
      ],
    }),
  }).then((response) => readApiResponse<TshirtMetadata>(response));

export const fetchSavedTshirtCollection = async (): Promise<
  readonly CollectionItem[]
> =>
  fetch("/api/tshirts", {
    method: "GET",
    credentials: "include",
    headers: createJsonHeaders(),
  })
    .then((response) => readApiResponse<TshirtListResponse>(response))
    .then((data) => data.tshirts.map(createCollectionItem))
    .catch((error: unknown) =>
      error instanceof AuthRequiredError ? [] : Promise.reject(error),
    );

export const saveProcessedTshirtImage = async (params: {
  readonly image: ProcessedTshirtImage;
  readonly onProgress: SaveImageProgress;
}): Promise<TshirtMetadata> => {
  params.onProgress("アップロードURLを発行しています");

  return requestUpload(params.image).then((upload) => {
    params.onProgress("R2へ画像をアップロードしています");

    return putImageToR2({
      image: params.image,
      upload,
    }).then(() => {
      params.onProgress("画像メタデータを保存しています");

      return createTshirtMetadata({
        image: params.image,
        upload,
      });
    });
  });
};
